import { Link } from 'react-router-dom';
import { TriangleAlert } from 'lucide-react';
import { seoConfig } from '../../configs/seoConfig';
import { getHoursSentence } from '../../configs/contactInfo';
import { useSiteConfig } from '../../configs/siteConfig';
import LegalLayout, { LegalContact } from '../terms/LegalLayout';

const NOT_COVERED = [
  ['Change of mind', 'If you change your mind after purchase, we cannot issue a refund.'],
  ['Wrong product purchase', 'If you purchased the wrong product by mistake, we cannot provide a refund or exchange.'],
  ['Lack of technical skills', "If you don't have the technical knowledge to use the product, this does not qualify for a refund."],
  ['Compatibility issues', 'If the product is not compatible with your system (unless explicitly stated otherwise in the product description).'],
  ['Duplicate purchases', 'Accidentally purchasing the same product multiple times does not qualify for a refund.'],
  ['Not meeting expectations', "If the product doesn't meet your expectations or requirements (when clearly described in the product details)."],
  ['Already downloaded or accessed', "Once you've downloaded or accessed the product, no refunds are possible."],
  ['Found a similar product elsewhere', 'Finding a similar or cheaper product elsewhere after purchase does not qualify for a refund.'],
];

const BEFORE_YOU_BUY = [
  ['Read the product description', 'Carefully review all product details, features, requirements, and specifications.'],
  ['Check previews', 'View all available previews, screenshots, and demo content if provided.'],
  ['Ask questions', 'Contact us if you have any questions or need clarification.'],
  ['Verify compatibility', 'Make sure the product is compatible with your system and requirements.'],
  ['Review requirements', 'Check all technical requirements and prerequisites needed to use the product.'],
  ['Read reviews', "Check customer reviews and ratings to understand others' experiences."],
];

const RefundPolicyPage = () => {
  const { companyName } = useSiteConfig().contactInfo;
  const HOURS = getHoursSentence();
  return (
    <LegalLayout
      title="Refund Policy"
      subtitle="Important information about refunds and returns for digital products."
      seo={seoConfig.pages.refund}
    >
      <div className="pk-alert" data-tone="warning" role="note">
        <p>
          <TriangleAlert aria-hidden="true" className="lg-alert-icon" />
          <strong>
            {companyName} operates a strict no-refund policy for all digital
            products. All sales are final and non-refundable.
          </strong>
        </p>
      </div>

      <h2>1. Policy Overview</h2>
      <p>
        Due to the nature of digital products, we do not offer refunds, returns,
        or exchanges under any circumstances. Once a purchase is completed and
        the product is delivered, the sale is considered final.
      </p>
      <p>
        This policy applies to all products available on our platform without
        exception. By making a purchase on {companyName}, you acknowledge and
        accept this no-refund policy.
      </p>

      <h2>2. Why We Don't Offer Refunds</h2>
      <p>Our no-refund policy exists for the following reasons:</p>
      <ul>
        <li>
          <strong>Digital nature of products:</strong> all our products are
          digital and can be accessed, downloaded, or copied as soon as they are
          delivered. Once delivered, we cannot "take back" the product.
        </li>
        <li>
          <strong>Access after verification:</strong> access is granted once
          your payment is verified, typically within 12 hours of purchase. From
          then on you have full access to the content.
        </li>
        <li>
          <strong>Fair business practice:</strong> since the products can be
          easily copied, offering refunds would expose us to potential abuse
          where customers could use the product and still request a refund.
        </li>
        <li>
          <strong>Industry standard:</strong> a no-refund policy for digital
          products is standard practice in the digital products industry.
        </li>
      </ul>

      <h2>3. Situations Not Covered by Refunds</h2>
      <p>The following situations do not qualify for refunds or exceptions:</p>
      <ul>
        {NOT_COVERED.map(([title, text]) => (
          <li key={title}>
            <strong>{title}:</strong> {text}
          </li>
        ))}
      </ul>

      <h2>4. Before You Purchase</h2>
      <p>
        To make sure you're making an informed decision, we strongly recommend
        the following before completing your purchase:
      </p>
      <ul>
        {BEFORE_YOU_BUY.map(([title, text]) => (
          <li key={title}>
            <strong>{title}:</strong> {text}
          </li>
        ))}
      </ul>

      <h2>5. What We Do Offer</h2>
      <p>While we don't offer refunds, we help in these ways:</p>
      <ul>
        <li>
          <strong>Technical support:</strong> help getting started with your
          purchase, for the support period stated in the product details
        </li>
        <li>
          <strong>Product updates:</strong> updates and bug fixes when available
        </li>
        <li>
          <strong>Documentation:</strong> the guides and documentation included
          with the product, where provided
        </li>
        <li>
          <strong>Pre-purchase help:</strong> answers to your questions before
          you buy, {HOURS}
        </li>
      </ul>

      <h2>6. Exceptional Circumstances</h2>
      <p>
        In very rare cases, we may consider providing support or assistance (but
        not refunds) for:
      </p>
      <ul>
        <li>
          <strong>Technical defects:</strong> if the product has a significant
          technical defect that makes it completely unusable and was not
          disclosed in the product description, we will work to fix the issue or
          provide an alternative solution.
        </li>
        <li>
          <strong>Delivery failure:</strong> if we fail to deliver the product
          within the stated timeframe and cannot resolve the issue, we may
          consider alternative arrangements.
        </li>
        <li>
          <strong>Billing errors:</strong> if there was a clear billing error on
          our part (for example, you were charged twice for the same product),
          we will correct the error.
        </li>
      </ul>
      <p>
        Even in these cases, we will first attempt to resolve the issue through
        product fixes, replacements, or technical support. Refunds are not
        guaranteed even in exceptional circumstances.
      </p>

      <h2>7. How to Get Help</h2>
      <p>
        If you're experiencing issues with a product or have concerns, contact
        us through the <Link to="/contact">Contact page</Link> or on WhatsApp.
        We're available {HOURS}, and we'll do our best to help resolve any issue
        you're having with the product.
      </p>

      <h2>8. Chargebacks and Disputes</h2>
      <div className="pk-alert" data-tone="warning">
        <p>
          <strong>Important notice:</strong> filing a chargeback or payment
          dispute with your bank or UPI app for a validly delivered digital
          product may result in:
        </p>
        <ul>
          <li>Immediate termination of your account</li>
          <li>Revocation of access to all purchased products</li>
          <li>Being banned from future purchases</li>
          <li>Legal action to recover costs and damages</li>
        </ul>
        <p>
          Please contact us first to resolve any issue before filing a dispute
          with your bank or payment provider.
        </p>
      </div>

      <h2>9. Changes to This Policy</h2>
      <p>
        We reserve the right to modify this refund policy at any time. Changes
        will be effective immediately upon posting. Your continued use of our
        services after changes constitutes acceptance of the modified policy.
      </p>

      <h2>10. Contact Information</h2>
      <p>
        For questions about this refund policy or to ask for help, contact us:
      </p>
      <LegalContact />

      <p className="lg-accept">
        <strong>
          By making a purchase on {companyName}, you acknowledge that you have
          read, understood, and agree to this no-refund policy.
        </strong>
      </p>
    </LegalLayout>
  );
};

export default RefundPolicyPage;
